/**
 * Language API Client
 *
 * Handles all language-related API calls to the backend.
 */

import * as api from './api.js';
import type { Language, LanguageCode, LanguageProgress } from '../../../shared/types';

// Re-export types for convenience
export type { Language, LanguageCode, LanguageProgress };

/**
 * Get all available (enabled) languages
 */
export async function fetchAvailableLanguages(): Promise<Language[]> {
  const response = await api.get<Language[]>('/languages');
  return response;
}

/**
 * Get a specific language by code
 */
export async function fetchLanguage(code: LanguageCode): Promise<Language> {
  const response = await api.get<Language>(`/languages/${code}`);
  return response;
}

/**
 * Get user's current language preference
 */
export async function fetchUserLanguagePreference(userId: string): Promise<{
  targetLanguage: LanguageCode;
  languagesStudied: LanguageCode[];
  currentProgress: LanguageProgress;
}> {
  const response = await api.get<{
    targetLanguage: LanguageCode;
    languagesStudied: LanguageCode[];
    currentProgress: LanguageProgress;
  }>(`/languages/users/${userId}/language`);
  return response;
}

/**
 * Set user's target language
 */
export async function updateUserLanguage(
  userId: string,
  targetLanguage: LanguageCode
): Promise<{
  targetLanguage: LanguageCode;
  progress: LanguageProgress;
  message: string;
}> {
  const response = await api.put<{
    targetLanguage: LanguageCode;
    progress: LanguageProgress;
    message: string;
  }>(`/languages/users/${userId}/language`, { targetLanguage });
  return response;
}

/**
 * Get all language progress for a user
 */
export async function fetchUserLanguageProgress(userId: string): Promise<{
  [languageCode: string]: LanguageProgress;
}> {
  const response = await api.get<{
    [languageCode: string]: LanguageProgress;
  }>(`/languages/users/${userId}/language-progress`);
  return response;
}
